import * as THREE from 'three';

export class TerrainMaterials {
    constructor() {
        this.materials = this.createMaterials();
    }
    
    createMaterials() {
        // Increase material reflectivity and brightness
        const enhanceVisibility = (material) => {
            material.roughness *= 0.8; // Reduce roughness by 20%
            material.metalness += 0.05; // Slight increase in metalness
            material.emissive = new THREE.Color(0x222222); // Slight self-illumination
            material.emissiveIntensity = 0.15; // Low intensity emission
            material.needsUpdate = true;
            return material;
        };
        
        const materials = {
            grass: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x4a9e4a, roughness: 0.8, metalness: 0.1 })), // Brighter green
            dirt: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x9c5623, roughness: 0.9, metalness: 0.0 })), // Slightly brighter brown
            stone: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x909090, roughness: 0.7, metalness: 0.1 })), // Lighter gray
            sand: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0xd2c290, roughness: 0.9, metalness: 0.0 })), // Brighter sand
            water: new THREE.MeshStandardMaterial({
                color: 0x0088ce, // Slightly brighter blue
                transparent: true,
                opacity: 0.7,
                roughness: 0.1,
                metalness: 0.2
            }),
            wood: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x9C5523, roughness: 0.8, metalness: 0.0 })), // Brighter wood
            leaves: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x32a032, roughness: 0.8, metalness: 0.0 })), // Brighter leaves
            cactus: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x3E9B67, roughness: 0.8, metalness: 0.0 })), // Brighter cactus
            tallGrass: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x8cfe10, roughness: 0.8, metalness: 0.0, side: THREE.DoubleSide })), // Brighter grass
            snow: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0xFFFFFF, roughness: 0.9, metalness: 0.0 })), // Keep snow bright
            
            // Stone and Structure Materials - Brightened versions
            stone_bricks: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.7, metalness: 0.1 })),
            mossy_stone: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x778866, roughness: 0.8, metalness: 0.1 })),
            cracked_stone: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x666666, roughness: 0.8, metalness: 0.1 })),
            decorated_stone: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.7, metalness: 0.1 })),
            polished_stone: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x999999, roughness: 0.4, metalness: 0.2 })),
            cobblestone: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x666666, roughness: 0.9, metalness: 0.1 })),
            dark_stone: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.8, metalness: 0.1 })),
            dark_stone_decorated: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x666666, roughness: 0.7, metalness: 0.1 })),
            stone_pillar: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.7, metalness: 0.1 })),
            stone_arch: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x777777, roughness: 0.7, metalness: 0.1 })),
            door_frame: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x886644, roughness: 0.8, metalness: 0.0 })),
            obsidian: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x332233 })),
            gold_ore: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0xFFD700 })),
            glowstone: new THREE.MeshStandardMaterial({ 
                color: 0xFFFF88,
                emissive: 0xFFFF00,
                emissiveIntensity: 0.8 // Increased from 0.5
            }),
            roots: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x765432 })),
            iron_bars: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x777777, roughness: 0.4, metalness: 0.8 })),
            
            // Dungeon specific materials - Brightened
            dungeon_floor: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x666666, roughness: 0.9, metalness: 0.1 })),
            dungeon_wall: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x777777, roughness: 0.7, metalness: 0.1 })),
            dungeon_ceiling: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.8, metalness: 0.1 })),
            dungeon_accent: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.6, metalness: 0.1 })),
            
            // Placeholder for unassigned
            default: enhanceVisibility(new THREE.MeshStandardMaterial({ color: 0xff00ff, roughness: 0.5, metalness: 0.5 }))
        };
        
        return materials;
    }
    
    getMaterial(name) {
        return this.materials[name] || this.materials.dirt;
    }
    
    getAllMaterials() {
        return this.materials;
    }
}
